import { Link, useRoute } from "wouter";
import { Home, LogOut, Globe } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/hooks/use-language";

interface GuestLayoutProps {
  children: React.ReactNode;
  slug: string;
  title?: string;
  showNav?: boolean;
}

export default function GuestLayout({ children, slug, title, showNav = true }: GuestLayoutProps) {
  const { lang, toggleLang } = useLanguage();
  const searchParams = new URLSearchParams(window.location.search);
  const token = searchParams.get("t");
  const tokenParam = token ? `?t=${token}` : '';

  const [isHome] = useRoute("/g/:slug");
  const [isCheckout] = useRoute("/g/:slug/checkout");

  const langLabel = lang === "en"
    ? "Switch language to Spanish"
    : "Cambiar idioma a ingles";

  return (
    <div className="min-h-[100dvh] bg-background" style={{ paddingBottom: showNav ? 'calc(3.5rem + env(safe-area-inset-bottom, 0px))' : '0' }}>
      <header className="sticky top-0 z-50 bg-background/80 backdrop-blur-md border-b px-4 flex items-center justify-between gap-2" style={{ height: 'calc(3.5rem + env(safe-area-inset-top, 0px))', paddingTop: 'env(safe-area-inset-top, 0px)' }}>
        <h1 className="font-display font-bold text-base truncate max-w-[220px]" data-testid="text-header-title">
          {title || (lang === "en" ? "Guest Portal" : "Portal de Huespedes")}
        </h1>
        <Button
          variant="outline"
          size="sm"
          onClick={toggleLang}
          className="font-mono text-xs flex-shrink-0"
          aria-label={langLabel}
          role="switch"
          aria-checked={lang === "es"}
          data-testid="button-toggle-lang"
        >
          <Globe className="w-3.5 h-3.5 mr-1" />
          {lang === 'en' ? 'ES' : 'EN'}
        </Button>
      </header>

      <main className="container max-w-md mx-auto px-4 py-5">
        {children}
      </main>

      {showNav && (
        <nav className="fixed bottom-0 left-0 right-0 bg-background/95 backdrop-blur-md border-t flex items-center justify-around z-50" style={{ height: 'calc(3.5rem + env(safe-area-inset-bottom, 0px))', paddingBottom: 'env(safe-area-inset-bottom, 0px)' }} data-testid="nav-bottom">
          <Link href={`/g/${slug}${tokenParam}`}>
            <div
              className={`flex flex-col items-center gap-0.5 py-2 px-6 cursor-pointer transition-colors ${isHome ? 'text-primary' : 'text-muted-foreground'}`}
              data-testid="nav-home"
              aria-label={lang === "en" ? "Home" : "Inicio"}
              aria-current={isHome ? "page" : undefined}
            >
              <Home className="w-5 h-5" />
              <span className="text-[10px] font-medium">
                {lang === 'en' ? 'Home' : 'Inicio'}
              </span>
            </div>
          </Link>
          <Link href={`/g/${slug}/checkout${tokenParam}`}>
            <div
              className={`flex flex-col items-center gap-0.5 py-2 px-6 cursor-pointer transition-colors ${isCheckout ? 'text-primary' : 'text-muted-foreground'}`}
              data-testid="nav-checkout"
              aria-label={lang === "en" ? "Checkout" : "Salida"}
              aria-current={isCheckout ? "page" : undefined}
            >
              <LogOut className="w-5 h-5" />
              <span className="text-[10px] font-medium">
                {lang === 'en' ? 'Checkout' : 'Salida'}
              </span>
            </div>
          </Link>
        </nav>
      )}
    </div>
  );
}
